import type { ApplyRowResult } from "../../../api/officeSlugBackfill.service.js";

/**
 * Builds the post-apply rollback CSV for the office slug backfill tool.
 *
 * The file is deliberately a valid rollback file for the NEIGHBOURING Office
 * Identity Backfill tool: its restore section matches columns by NAME, needs
 * `officeId`, `field` and `previous`, ignores any extra columns, and reads an
 * empty `previous` cell as "clear the field". Every row this tool writes was
 * NULL before the run — so a rollback row is simply previous="" — and a bad
 * run can be undone by uploading this file into that tool's restore section.
 * `officeName` and `current` (the slug that was generated) are extra columns
 * for the human reviewing the file; the parser skips them.
 *
 * The one deliberate naming choice: the restore-value column is `previous`,
 * not `proposed`, because that is the name the sibling's parser requires —
 * `parseRollback` rejects a file without a `previous` column.
 */

export const ROLLBACK_HEADERS = ["officeId", "officeName", "field", "current", "previous"] as const;

/**
 * Leading characters that make Excel and Sheets treat a cell as a formula.
 * Office names come from the database, i.e. are untrusted as far as this
 * export is concerned, so a triggering cell is prefixed to read as text.
 */
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Quotes a field only when it needs it, and doubles any embedded quote —
 * office names carry commas and quotes.
 *
 * A field starting with a formula trigger is prefixed with a single quote so
 * the spreadsheet reads it as text. Quoting alone does not prevent this: Excel
 * strips the quotes and evaluates what is inside.
 */
function csvField(value: string | null): string {
  let text = value === null ? "" : value;
  if (FORMULA_TRIGGERS.some(trigger => text.startsWith(trigger))) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * One row per office the run actually wrote (status "generated") — those are
 * the only rows there is anything to undo. `current` is the slug the run
 * generated; `previous` is always empty because the backfilled offices held
 * NULL, and restoring an empty cell clears the field.
 *
 * @param nameFor Resolves an office name when the apply result carries none.
 */
export function buildRollbackCsv(
  results: ApplyRowResult[],
  nameFor: (result: ApplyRowResult) => string | null,
): string {
  const lines: string[] = [ROLLBACK_HEADERS.join(",")];
  for (const result of results) {
    if (result.status !== "generated") {
      continue;
    }
    lines.push(
      [result.officeId, result.officeName ?? nameFor(result), "slug", result.slug ?? null, ""]
        .map(csvField)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/** `2026-08-12-10-20-30` filename stamp, matching the sibling tool's files. */
function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function rollbackFilename(now: Date): string {
  return `office-slug-backfill-rollback-${filenameStamp(now)}.csv`;
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Builds the rollback CSV and hands it to the browser.
 *
 * @param at Stamps the filename — pass the moment of the download, so two
 *   downloads in one session cannot produce identically named files.
 */
export function downloadRollbackCsv(
  results: ApplyRowResult[],
  nameFor: (result: ApplyRowResult) => string | null,
  at: Date,
): void {
  downloadCsv(buildRollbackCsv(results, nameFor), rollbackFilename(at));
}
